import React from "react";
import { ConfigProvider } from "antd";
import arEG from "antd/locale/ar_EG";
import enUS from "antd/locale/en_US";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DashboardAntDatePicker } from "src/components/reusable/antdDatePicker/DashboardAntDatePicker";
import { getNumberLocale } from "src/utils/numberFormat";
import "./DateRangePicker.scss";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onRangeChange: (range: { start: string; end: string }) => void;
  maxPastMonths?: number;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onRangeChange,
}) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("ar");
  const antdLocale = isAr ? arEG : enUS;
  const locale = React.useMemo(
    () => getNumberLocale(i18n.language),
    [i18n.language]
  );
  const [localStart, setLocalStart] = React.useState(
    startDate ? dayjs(startDate, "YYYY-MM-DD") : null
  );
  const [localEnd, setLocalEnd] = React.useState(
    endDate ? dayjs(endDate, "YYYY-MM-DD") : null
  );

  const handleStartChange = (date: dayjs.Dayjs | null) => {
    setLocalStart(date);
    // Do not call onRangeChange yet
  };

  const handleEndChange = (date: dayjs.Dayjs | null) => {
    setLocalEnd(date);
    if (localStart && date) {
      onRangeChange({
        start: localStart.format("YYYY-MM-DD"),
        end: date.format("YYYY-MM-DD"),
      });
    }
  };

  const formatDisplayDate = React.useCallback(
    (date: dayjs.Dayjs) => {
      if (isAr) {
        return new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(date.toDate());
      }
      return date.format("DD-MM-YYYY");
    },
    [isAr, locale]
  );

  return (
    <ConfigProvider locale={antdLocale} direction={isAr ? "rtl" : "ltr"}>
      <div className="date-range-picker-container">
        <DashboardAntDatePicker
          value={localStart}
          format={isAr ? formatDisplayDate : "DD-MM-YYYY"}
          onChange={handleStartChange}
          placeholder={t("organicMedia.datePicker.startDate")}
          className="dashboard-date-picker"
          placement={isAr ? "bottomRight" : "bottomLeft"}
          disabledDate={() => false}
        />

        <DashboardAntDatePicker
          value={localEnd}
          format={isAr ? formatDisplayDate : "DD-MM-YYYY"}
          onChange={handleEndChange}
          placeholder={t("organicMedia.datePicker.endDate")}
          className="dashboard-date-picker"
          placement={isAr ? "bottomRight" : "bottomLeft"}
          disabledDate={date => (localStart ? date.isBefore(localStart, "day") : false)}
        />
      </div>
    </ConfigProvider>
  );
};

export default DateRangePicker;
